import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money, relative, toBase } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { FileText, Plus, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function QuotationsTab({ lead, users }) {
  const { can } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const umap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  const { data: quotations = [], isLoading } = useQuery({
    queryKey: ["quotations", lead.id], queryFn: () => get(`/crm/quotations?lead_id=${lead.id}`),
  });
  const { data: rates } = useQuery({ queryKey: ["exchange-rates"], queryFn: () => get("/crm/exchange-rates") });

  const startNew = () => navigate(`/crm/leads/${lead.id}/quotation/new`);
  const remove = useMutation({
    mutationFn: (qid) => del(`/crm/quotations/${qid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["quotations", lead.id] }); toast.success("Quotation deleted"); },
  });

  const sorted = [...quotations].sort((a, b) => (b.version || 0) - (a.version || 0));
  const total = (q) => (q.line_items || []).reduce((s, li) => s + li.qty * li.rate * (1 - (li.discount || 0) / 100) * (1 + (li.tax || 0) / 100), 0);

  if (isLoading) return null;

  return (
    <div>
      {can("crm", "create") && (
        <Button size="sm" variant="outline" className="mb-3" data-testid="new-quotation-btn" onClick={startNew}>
          <Plus className="mr-1 h-3.5 w-3.5" />New Quotation
        </Button>
      )}
      {sorted.length === 0 ? <EmptyState icon={FileText} title="No quotations yet" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="quotations-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2.5 font-semibold">Version</th>
                <th className="px-3 py-2.5 font-semibold">Created</th>
                <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">By</th>
                <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((q) => {
                const amt = total(q);
                const base = toBase(amt, q.currency, rates);
                return (
                  <tr key={q.id} data-testid={`quotation-row-${q.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-3 py-2.5 font-medium">v{q.version || 1}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{relative(q.created_at)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="font-medium">{money(amt, q.currency)}</div>
                      {rates?.base && q.currency !== rates.base && <div className="text-[11px] text-muted-foreground">≈ {money(base, rates.base)}</div>}
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge status={q.status} /></td>
                    <td className="px-3 py-2.5"><Avatar user={umap[q.created_by]} size="xs" /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`view-quotation-${q.id}`}
                          onClick={() => navigate(`/crm/leads/${lead.id}/quotation/${q.id}`)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {can("crm", "delete") && (
                          q.status === "draft" ? (
                            <ConfirmDelete testid={`del-quotation-${q.id}`} title={`Delete v${q.version}?`}
                              trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                              onConfirm={() => remove.mutate(q.id)} />
                          ) : (
                            <Button variant="ghost" size="icon" disabled title="Only draft quotations can be deleted">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
